import type { PropertyFilters } from '~/core/entities/property/filters';
import type { EmptyObject } from '~~/type-utils';
import type { RouteLocationRaw } from 'vue-router';

import {
	FeatureEnum,
	PropertyTypeEnum,
	PropertyTypeSlug,
	propertyTypesSlugByValue,
	propertyTypesValueBySlug
} from '~/core/entities/CRMEnums';
import { deduplicateRegions } from '~/utils';
import { slugify } from '~~/utils';

export type TransactionTypeSlug = 'venda' | 'aluguel';

type ParsedSlugs = {
	addressSlug?: string;
	locationSlug?: string;
	propertyTypeSlug?: string;
};

const transactionTypeSlugsByValue: Record<
	NonNullable<PropertyFilters['transactionType']>,
	TransactionTypeSlug
> = {
	sell: 'venda',
	rent: 'aluguel'
} as const;

const queryStringFilters = [
	'transactionType',
	'searchTerm',
	'locationSlug',
	'isExclusive',
	'minArea',
	'maxArea',
	'minAskingPrice',
	'maxAskingPrice',
	'minRentPrice',
	'maxRentPrice',
	'minSquareMeterPrice',
	'maxSquareMeterPrice',
	'minCondoFee',
	'maxCondoFee',
	'minTax',
	'maxTax',
	'suites',
	'bedrooms',
	'bathrooms',
	'parkingSpots',
	'features',
	'propertyTypes',
	'commercialIds',
	'h3Ids',
	'regions',
	'cities',
	'states',
	'macroregions',
	'sortBy',
	'orderBy',
	'pluscodesGroup',
	'addressSlug',
	'poiLocationLat',
	'poiLocationLng',
	'bounds',
	'zoom',
	'includeH3Clusters'
] as const;

type QueryStringPropertyFilter = (typeof queryStringFilters)[number];
export type PageName = 'imoveis' | 'mapa-de-imoveis' | 'colecao' | 'colecoes';

export type LocationFiltersInput = {
	states?: string[] | string;
	cities?: string[] | string;
	regions?: string[] | string;
	propertyTypes?: PropertyTypeEnum[] | PropertyTypeEnum;
	features?: FeatureEnum[] | FeatureEnum;
} & Partial<
	Omit<
		PropertyFilters,
		'states' | 'cities' | 'regions' | 'propertyTypes' | 'features'
	>
>;

export class PropertyFiltersPresenter {
	static DEFAULT_URL_PATH = '/venda/imoveis/sao-paulo-sp-brasil';
	static DEFAULT_MAP_URL_PATH = '/venda/mapa-de-imoveis/sao-paulo-sp-brasil';

	static #stringArrayFilters = [
		'features',
		'propertyTypes',
		'commercialIds',
		'h3Ids',
		'regions',
		'cities',
		'states',
		'macroregions',
		'pluscodesGroup'
	] as const;

	static #numericFilters = [
		'minArea',
		'maxArea',
		'minAskingPrice',
		'maxAskingPrice',
		'minRentPrice',
		'maxRentPrice',
		'minSquareMeterPrice',
		'maxSquareMeterPrice',
		'minCondoFee',
		'maxCondoFee',
		'minTax',
		'maxTax',
		'suites',
		'bedrooms',
		'bathrooms',
		'parkingSpots',
		'poiLocationLat',
		'poiLocationLng',
		'zoom'
	] as const;

	static #booleanFilters = [
		'isExclusive',
		'onlyPluscodes',
		'includeH3Clusters'
	] as const;

	static #stringFilters = [
		'transactionType',
		'locationSlug',
		'pluscodesGroup',
		'sortBy',
		'orderBy',
		'bounds'
	] as const;

	static #slashFilters = ['address'] as const;

	static #getLocations({
		states,
		cities,
		regions
	}: Pick<PropertyFilters, 'states' | 'cities' | 'regions'>): {
		state: string;
		city: string;
		firstRegion: string;
		remainingRegions: string[];
	} {
		const state = states?.[0] || '';
		const city = state ? cities?.[0] || '' : '';

		const [firstRegion = '', ...remainingRegions] =
			city && regions ? regions : [];

		return { city, state, firstRegion, remainingRegions };
	}

	/**
	 * @param filters Filters to be converted to a path
	 *
	 * When defining a location filter, the less specific location
	 * should be present in order for the most specific location
	 * to be used; For example, while filtering by a city name,
	 * the state name should also be defined, and while filtering
	 * by a region name, the city and state names should also be defined.
	 * @param extraQueryStrings Extra query strings to be added to the path
	 * @param page Page name to be used in the path
	 * @returns Path string containing the filters
	 */
	static getPathFromFilters(
		{
			transactionType,
			propertyTypes,
			states,
			cities,
			regions,
			locationSlug: locationSlugFilter,
			addressSlug,
			poiLocationLat,
			poiLocationLng,
			...otherFilters
		}: Partial<PropertyFilters>,
		extraQueryStrings: Record<string, any> = {},
		{
			page = 'imoveis',
			collection
		}: {
			page?: PageName;
			collection?: string;
		} = {}
	): string {
		const self = PropertyFiltersPresenter;

		const transactionTypeSlug =
			self.getSlugFromTransactionType(transactionType);

		const { state, city, firstRegion, remainingRegions } = self.#getLocations({
			states,
			cities,
			regions
		});

		const defaultLocationSlug = 'sao-paulo-sp-brasil';

		const locationSlugParts = [firstRegion, city, state]
			.filter(Boolean)
			.map(slugify);

		const locationSlug = locationSlugParts.join('')
			? [...locationSlugParts, 'brasil'].join('-')
			: locationSlugFilter || defaultLocationSlug;

		if (!addressSlug && locationSlugFilter?.includes('/')) {
			const [prevAddressSlug] = locationSlugFilter.split('/') || [];
			addressSlug = prevAddressSlug;
		}

		if (addressSlug) {
			const addressParts = addressSlug.split('/');
			addressSlug = addressParts
				.filter(
					part => !PropertyFiltersPresenter.#isAValidPropertyTypeSlug(part)
				)
				.join('/');
		}

		const [firstPropertyType = '', ...remainingPropertyTypes] =
			propertyTypes || [];

		const propertyTypeSlug = firstPropertyType
			? propertyTypesSlugByValue[firstPropertyType]
			: null;

		const queryStringObject = new URLSearchParams();

		const nowShownFilters = ['page', 'perPage', 'wishlistId'] as const;

		for (const field of nowShownFilters) {
			if (Object.prototype.hasOwnProperty.call(otherFilters, field)) {
				delete (otherFilters as Record<string, unknown>)[field];
			}
		}

		const [firstCity = ''] = cities || [];

		const queryStringFiltersObject = {
			...otherFilters,
			regions: firstRegion
				? [firstRegion, ...remainingRegions] // isso aqui é necessário para manter a região do slug na querystring, evitando problema no componente de região
				: remainingRegions,
			propertyTypes: remainingPropertyTypes.map(
				propertyType => propertyTypesSlugByValue[propertyType]
			),
			cities: firstCity,
			...(poiLocationLat ? { poiLocationLat } : {}),
			...(poiLocationLng ? { poiLocationLng } : {})
		};

		const allQueryStrings = {
			...queryStringFiltersObject,
			...extraQueryStrings
		};

		for (const [key, value] of Object.entries(allQueryStrings)) {
			if (Array.isArray(value)) {
				if (value.length > 0) {
					queryStringObject.set(key, value.join(','));
				}
				continue;
			}

			if (value || value === 0) queryStringObject.set(key, value.toString());
		}

		const queryString = queryStringObject.toString()
			? `?${queryStringObject.toString()}`
			: '';

		if (page === 'colecao') {
			// Curated list page: /colecao/{slug}
			if (!collection) {
				return `/colecao${queryString}`;
			}
			return `/colecao/${collection}${queryString}`;
		}

		if (page === 'colecoes') {
			// User wishlists page: /colecoes/minha-colecao or /colecoes/compartilhada/{wishlistSlug}
			if (!collection) {
				return `/colecoes${queryString}`;
			}
			if (collection === 'minha-colecao') {
				return `/colecoes/minha-colecao${queryString}`;
			}
			return `/colecoes/compartilhada/${collection}${queryString}`;
		}

		const addressSegment = addressSlug
			? `/${encodeURIComponent(addressSlug)}`
			: '';

		if (propertyTypeSlug) {
			return `/${transactionTypeSlug}/${page}/${locationSlug}${addressSegment}/${propertyTypeSlug}${queryString}`;
		}

		return `/${transactionTypeSlug}/${page}/${locationSlug}${addressSegment}${queryString}`;
	}

	/**
	 * @param raw Slug path to be parsed
	 * @returns Parsed slugs
	 *
	 * Ex.:
	 *  - /bairro                             → { locationSlug }
	 *  - /bairro/casa                        → { locationSlug, propertyTypeSlug }
	 *  - /bairro/rua                         → { locationSlug, addressSlug }
	 *  - /bairro/casa/rua                    → { locationSlug, propertyTypeSlug, addressSlug }
	 */
	static parseSlugPath(raw?: string): ParsedSlugs {
		const parts = (raw ?? '').split('/').filter(Boolean);

		if (parts.length === 0) return {};

		const [locationSlug, ...remainingParts] = parts;

		if (remainingParts.length === 0) return { locationSlug };

		const lastPart = remainingParts[remainingParts.length - 1];
		const lastPartIsPropertyType =
			lastPart && PropertyFiltersPresenter.#isAValidPropertyTypeSlug(lastPart);

		const addressSegments = lastPartIsPropertyType
			? remainingParts.slice(0, -1)
			: remainingParts;

		let addressSlug: string | undefined;
		if (addressSegments.length) {
			addressSlug = decodeURIComponent(addressSegments.join('/'));
		}

		return {
			locationSlug,
			...(lastPartIsPropertyType ? { propertyTypeSlug: lastPart } : {}),
			...(addressSlug ? { addressSlug } : {})
		};
	}

	/**
	 * @param filters Filters to be converted to a path
	 *
	 * When defining a location filter, the less specific location
	 * should be present in order for the most specific location
	 * to be used; For example, while filtering by a city name,
	 * the state name should also be defined, and while filtering
	 * by a region name, the city and state names should also be defined.
	 * @param options.page Page name to be used in the path
	 * @returns {RouteLocationRaw} Route object containing path and query values that represent the filters
	 */
	static getRouteObjectFromFilters(
		filters: Partial<PropertyFilters>,
		{
			page = 'imoveis',
			extraQueryStrings
		}: { page?: PageName; extraQueryStrings?: Record<string, any> } = {}
	): RouteLocationRaw {
		const fullPath = PropertyFiltersPresenter.getPathFromFilters(
			filters,
			extraQueryStrings,
			{ page }
		);

		const [path, queryString] = fullPath.split('?');

		if (!queryString) return { path };

		const queryObject = Object.fromEntries(
			new URLSearchParams(queryString).entries()
		);

		return {
			path,
			query: queryObject
		};
	}

	static getFiltersFromPath(
		path: string,
		previousFilters?: PropertyFilters,
		page: PageName = 'imoveis'
	): PropertyFilters {
		const pathPrefixRegex =
			page === 'colecao'
				? new RegExp(`^/colecao/[a-z0-9-]+/?`)
				: page === 'colecoes'
					? new RegExp(`^/colecoes/[a-z0-9-]+/?`)
					: new RegExp(`^/(venda|aluguel)/${page}/?`);

		const isValidPropertiesFiltersPath = pathPrefixRegex.test(path);

		if (!isValidPropertiesFiltersPath) return { transactionType: 'sell' };

		// Handle different URL structures for different page types
		let transactionTypeSlug: TransactionTypeSlug | undefined;
		let locationAndPropertyTypeSlugWithQueryString: string[];

		if (page === 'colecoes') {
			// For collections: /colecoes/wishlistId?bounds=...
			const pathAfterPage = path.replace(new RegExp(`^/${page}/`), '');
			transactionTypeSlug = 'venda'; // Collections are always sell
			locationAndPropertyTypeSlugWithQueryString = [pathAfterPage || ''];
		} else {
			// For normal pages: /venda/imoveis/location?bounds=... (keep original behavior)
			const parts = path.replace(/^\//, '').split(`/${page}/`);
			transactionTypeSlug = parts[0] as TransactionTypeSlug;
			locationAndPropertyTypeSlugWithQueryString = parts
				.slice(1)
				.filter(Boolean);
		}

		if (!transactionTypeSlug) return { transactionType: 'sell' };

		const [locationAndPropertyTypeSlugsString, queryString] =
			locationAndPropertyTypeSlugWithQueryString.join().split('?') || ['', ''];

		// Handle URL parsing: for collections extract wishlistId, for normal pages parse location/property slugs
		let addressSlug: string | undefined;
		let locationSlug: string | undefined;
		let propertyTypeSlug: string | undefined;

		if (page === 'colecoes') {
			// For collections: extract wishlistId from URL path and set defaults
			locationSlug = undefined;
		} else {
			// For normal pages: parse location and property type slugs
			({ addressSlug, locationSlug, propertyTypeSlug } =
				PropertyFiltersPresenter.parseSlugPath(
					locationAndPropertyTypeSlugsString
				));
		}

		const queryStringObject = new URLSearchParams(queryString);

		const queryStringPropertyTypes =
			queryStringObject.get('propertyTypes')?.split(',') || [];

		// Verificar se estamos usando um dos slugs de tipo de propriedade válidos
		const isPropertyTypeSlug = (
			slug: string | undefined | null
		): slug is PropertyTypeSlug => {
			return !!slug && PropertyFiltersPresenter.#isAValidPropertyTypeSlug(slug);
		};

		const allPropertyTypes = [propertyTypeSlug, ...queryStringPropertyTypes]
			.filter(isPropertyTypeSlug)
			.map(slug => propertyTypesValueBySlug[slug]);

		const queryStringFilters =
			PropertyFiltersPresenter.#getFiltersFromQueryString(queryString || '');

		if (addressSlug) {
			queryStringFilters.addressSlug = addressSlug;
		}

		// Extract wishlistId for collections from URL path
		if (page === 'colecoes' && locationAndPropertyTypeSlugsString) {
			let wishlistId = locationAndPropertyTypeSlugsString.trim();
			// Handle new shared collection URL structure: compartilhada/wishlistId
			if (wishlistId.startsWith('compartilhada/')) {
				wishlistId = wishlistId.replace('compartilhada/', '');
			}
			// Handle legacy minha-colecao URL
			if (wishlistId !== 'minha-colecao' && wishlistId) {
				(queryStringFilters as any).wishlistId = wishlistId;
			}
		}

		if (queryStringFilters.regions) {
			queryStringFilters.regions = deduplicateRegions(
				queryStringFilters.regions
			);
		}

		const result = {
			...queryStringFilters,
			propertyTypes: allPropertyTypes,
			transactionType:
				PropertyFiltersPresenter.getTransactionTypeFromSlug(
					transactionTypeSlug
				),
			locationSlug: locationSlug || 'sao-paulo-sp-brasil'
		};

		// Para páginas de coleções, remover filtros de localização desnecessários
		if (page === 'colecoes') {
			const collectionResult = { ...result };
			delete (collectionResult as any).locationSlug;
			delete (collectionResult as any).states;
			delete (collectionResult as any).cities;
			delete (collectionResult as any).regions;
			delete (collectionResult as any).macroregions;
			return collectionResult;
		}

		return result;
	}

	static #getFiltersFromQueryString(
		queryString: string
	): Record<QueryStringPropertyFilter, any> | EmptyObject {
		if (!queryString.trim()) return {};

		const queryStringObject = new URLSearchParams(queryString);

		const filters: Record<string, any> = {};

		for (const key of queryStringObject.keys()) {
			const valuesForCurrentKey = queryStringObject.getAll(key);

			if (!PropertyFiltersPresenter.#isQueryStringFilter(key)) continue;

			if (PropertyFiltersPresenter.#isStringArrayFilter(key)) {
				const values: string[] = valuesForCurrentKey
					.flatMap(value => value.split(','))
					.filter(value => value.trim());

				if (values.length > 0) {
					if (key === 'regions') {
						const locationSlug = filters.locationSlug as string | undefined;
						filters[key] = values.filter(region => {
							const slugifiedRegion = slugify(region);
							return !locationSlug?.startsWith(slugifiedRegion);
						});
					} else {
						filters[key] = values;
					}
				}

				continue;
			}

			if (PropertyFiltersPresenter.#isNumericFilter(key)) {
				const [firstNumericValue] = valuesForCurrentKey
					.map(Number)
					.filter(number => !Number.isNaN(number));

				if (typeof firstNumericValue === 'number') {
					filters[key] = firstNumericValue;
				}

				continue;
			}

			if (PropertyFiltersPresenter.#isBooleanFilter(key)) {
				const [firstBooleanValue] = valuesForCurrentKey
					.map(value => {
						if (value === 'true') return true;
						if (value === 'false') return false;

						return null;
					})
					.filter(value => value !== null);

				if (typeof firstBooleanValue === 'boolean') {
					filters[key] = firstBooleanValue;
				}

				continue;
			}

			if (PropertyFiltersPresenter.#isStringFilter(key)) {
				const [firstStringValue] = valuesForCurrentKey;

				if (firstStringValue) {
					filters[key] = firstStringValue;
				}

				continue;
			}

			if (PropertyFiltersPresenter.#isSlashFilter(key)) {
				const [firstSlashValue] = valuesForCurrentKey;

				if (firstSlashValue) {
					filters[key] = firstSlashValue;
				}
			}
		}

		return filters;
	}

	static #isStringArrayFilter(key: string): boolean {
		return PropertyFiltersPresenter.#stringArrayFilters.includes(key as any);
	}

	static #isNumericFilter(key: string): boolean {
		return PropertyFiltersPresenter.#numericFilters.includes(key as any);
	}

	static #isBooleanFilter(key: string): boolean {
		return PropertyFiltersPresenter.#booleanFilters.includes(key as any);
	}

	static #isStringFilter(key: string): boolean {
		return PropertyFiltersPresenter.#stringFilters.includes(key as any);
	}

	static #isQueryStringFilter(key: string): key is QueryStringPropertyFilter {
		return queryStringFilters.includes(key as QueryStringPropertyFilter);
	}

	static #isSlashFilter(key: string): boolean {
		return PropertyFiltersPresenter.#slashFilters.includes(key as any);
	}

	static #isAValidPropertyTypeSlug(slug: string): slug is PropertyTypeSlug {
		return slug in propertyTypesValueBySlug;
	}

	static getPropertyTypeFromSlug(
		slug: PropertyTypeSlug | string | null | undefined
	): PropertyTypeEnum | null {
		if (!slug) return null;
		if (!PropertyFiltersPresenter.#isAValidPropertyTypeSlug(slug)) return null;

		return propertyTypesValueBySlug[slug];
	}

	static #transactionTypesBySlug: Record<
		TransactionTypeSlug,
		PropertyFilters['transactionType']
	> = {
		venda: 'sell',
		aluguel: 'rent'
	} as const;

	static getSlugFromTransactionType(
		transactionType: PropertyFilters['transactionType'] | null | undefined
	): TransactionTypeSlug {
		return transactionTypeSlugsByValue[transactionType || 'sell'] || 'venda';
	}

	static getTransactionTypeFromSlug(
		slug: TransactionTypeSlug | null | undefined
	): PropertyFilters['transactionType'] {
		if (!slug) return 'rent';

		return PropertyFiltersPresenter.#transactionTypesBySlug[slug] || 'sell';
	}

	/**
	 * Prepares filters for API request by applying specific rules
	 * for POI location-related filters.
	 *
	 * @param currentFilters - The current filters to be prepared
	 * @param options - Optional configuration
	 * @returns The prepared filters for the request
	 */
	static prepareFiltersForRequest(
		currentFilters: PropertyFilters,
		options?: { includeH3Clusters?: boolean }
	): PropertyFilters {
		const hasCoords = Boolean(
			currentFilters.poiLocationLat && currentFilters.poiLocationLng
		);
		const addressSlug = hasCoords ? undefined : currentFilters.addressSlug;
		const locationSlug = hasCoords ? undefined : currentFilters.locationSlug;
		const sortBy =
			(currentFilters.addressSlug || hasCoords) &&
			(currentFilters.sortBy === 'default_ordenation' || !currentFilters.sortBy)
				? 'distance'
				: currentFilters.sortBy;

		const hasH3Ids = currentFilters.h3Ids && currentFilters.h3Ids.length > 0;
		const bounds = hasH3Ids ? undefined : currentFilters.bounds;

		const result = {
			...currentFilters,
			sortBy,
			addressSlug,
			locationSlug,
			bounds
		};

		if (options?.includeH3Clusters) {
			result.includeH3Clusters = true;
		}

		return result;
	}

	/**
	 * Creates a base location filter object with the provided location data.
	 *
	 * @param options - The location options and other filters
	 * @returns A PropertyFilters object with the location filters, default transaction type, and other filters
	 */
	static createLocationFilters({
		states,
		cities,
		regions,
		propertyTypes,
		features,
		...otherFilters
	}: LocationFiltersInput): PropertyFilters {
		const normalize = <T>(value: T | T[] | undefined): T[] =>
			value ? (Array.isArray(value) ? value : [value]).filter(Boolean) : [];

		const addIfExists = <T>(key: string, value: T[]): Record<string, T[]> =>
			value.length ? { [key]: value } : {};

		return {
			states: normalize(states),
			cities: normalize(cities),
			regions: normalize(regions),
			transactionType: 'sell',
			...otherFilters,
			...addIfExists('propertyTypes', normalize(propertyTypes)),
			...addIfExists('features', normalize(features))
		};
	}

	/**
	 * Retrieves the keys of filters that are considered applied based on their type and value.
	 *
	 * This method iterates over the keys of the provided `filters` object and checks each key's value
	 * to determine if it represents an applied filter. The type of the filter determines the criteria:
	 *
	 * - **Boolean filters**: The value must be `true`.
	 * - **String array filters**: The array must have a length greater than 0.
	 * - **Numeric filters**: The value must be greater than 0.
	 * - **String filters**: The string must have a length greater than 0.
	 *
	 * @param filters - An object containing filter properties and their values.
	 * @returns An array of keys for the filters that are applied.
	 *
	 * */
	static getAppliedFilterKeys(
		filters: PropertyFilters
	): (keyof PropertyFilters)[] {
		const appliedFilterKeys: (keyof PropertyFilters)[] = [];

		for (const key of Object.keys(filters) as (keyof PropertyFilters)[]) {
			if (
				PropertyFiltersPresenter.#isBooleanFilter(key) &&
				filters[key as keyof PropertyFilters]
			) {
				appliedFilterKeys.push(key);
				continue;
			}

			if (
				PropertyFiltersPresenter.#isStringArrayFilter(key) &&
				(filters[key as keyof PropertyFilters] as string[]).length > 0
			) {
				appliedFilterKeys.push(key);
				continue;
			}

			if (
				PropertyFiltersPresenter.#isNumericFilter(key) &&
				(filters[key as keyof PropertyFilters] as number) > 0
			) {
				appliedFilterKeys.push(key);
				continue;
			}

			if (
				PropertyFiltersPresenter.#isStringFilter(key) &&
				(filters[key as keyof PropertyFilters] as string).length > 0
			) {
				appliedFilterKeys.push(key);
				continue;
			}

			if (
				PropertyFiltersPresenter.#isSlashFilter(key) &&
				(filters[key as keyof PropertyFilters] as string).length > 0
			) {
				appliedFilterKeys.push(key);
				continue;
			}

			continue;
		}

		return appliedFilterKeys;
	}
}
